import { ThunkDispatch } from 'redux-thunk';
import { ReduxState } from '../../state';
import { ReduxAction } from '../../actions';
import { selectKind } from '../../location/selectors.common';
import { ROUTE_COURSE_ADMIN, ROUTE_COURSE_PAGE } from '../../routeActions';
import {
  selectCurrentCoursePath,
  selectIsAdminOfCurrentCourse,
} from '../../Courses/selectors';
import { currentCohortPathSelector } from '../../Auth/selectors';
import { selectCurrentInstitutionPath } from '../../Institutions/CurrentInstitutionPath/selectors';
import { courseCodeFromPath } from '../../../ReduxStorage/helpers/paths';

declare global {
  interface Window {
    userPilotNavigateToCourseOutcomes: () => void;
    userPilotNavigateToCourseContent: () => void;
    userPilotNavigateToCourseHomepage: () => void;
    userPilotNavigateToCourseAppearance: () => void;
    userPilotNavigateToCourseLandingpage: () => void;
    userPilotNavigateToCourseGeneral: () => void;
  }
}

export const onPageArrive = ({
  userpilot,
  dispatch,
  getState,
}: {
  userpilot: {
    reload: () => (
      dispatch: ThunkDispatch<ReduxState, void, ReduxAction>
    ) => void;
  };
  dispatch: ThunkDispatch<ReduxState, void, ReduxAction>;
  getState: () => ReduxState;
}): void => {
  const locationKind = selectKind(getState());

  // need to trigger userpilot reload for SPA:
  // https://docs.userpilot.com/article/22-install-userpilot-on-single-page-application-frameworks
  // no need to reload userpilot if it's the first route
  if (locationKind !== 'load') {
    dispatch(userpilot.reload());
  }
};

export const initialiseCourseNavigationForUserpilot = (
  dispatch: ThunkDispatch<ReduxState, void, ReduxAction>,
  getState: () => ReduxState
): void => {
  const coursePath = selectCurrentCoursePath(getState());
  const cohortPath = currentCohortPathSelector(getState());
  const institutionPath = selectCurrentInstitutionPath(getState());

  window.userPilotNavigateToCourseOutcomes = () => {
    if (!selectIsAdminOfCurrentCourse(getState())) return;

    // go to "Course Setup > Outcomes"
    dispatch({
      type: ROUTE_COURSE_ADMIN,
      query: {
        action: 'outcomes',
        course: coursePath,
        cohort: cohortPath,
      },
    });
  };
  window.userPilotNavigateToCourseContent = () => {
    if (!selectIsAdminOfCurrentCourse(getState())) return;

    // go to "Course Setup > Content"
    dispatch({
      type: ROUTE_COURSE_ADMIN,
      query: {
        action: 'content',
        course: coursePath,
        cohort: cohortPath,
      },
    });
  };
  window.userPilotNavigateToCourseHomepage = () => {
    if (!selectIsAdminOfCurrentCourse(getState())) return;

    // go to "Course Homepage"
    const courseCode = courseCodeFromPath(coursePath);
    if (courseCode) {
      dispatch({
        type: ROUTE_COURSE_PAGE,
        params: {
          courseCode,
          subPath: 'homepage',
        },
        query: {},
        state: {
          institutionPrefix: institutionPath,
        },
      });
    }
  };
  window.userPilotNavigateToCourseAppearance = () => {
    if (!selectIsAdminOfCurrentCourse(getState())) return;

    // go to "Course Setup > Appearance"
    dispatch({
      type: ROUTE_COURSE_ADMIN,
      query: {
        action: 'appearance',
        course: coursePath,
        cohort: cohortPath,
      },
    });
  };
  window.userPilotNavigateToCourseLandingpage = () => {
    if (!selectIsAdminOfCurrentCourse(getState())) return;

    // go to "Course Setup > Landing Page"
    dispatch({
      type: ROUTE_COURSE_ADMIN,
      query: {
        action: 'landing_page',
        course: coursePath,
        cohort: cohortPath,
      },
    });
  };
  window.userPilotNavigateToCourseGeneral = () => {
    if (!selectIsAdminOfCurrentCourse(getState())) return;

    // go to "Course Setup > General"
    dispatch({
      type: ROUTE_COURSE_ADMIN,
      query: {
        action: 'general',
        course: coursePath,
        cohort: cohortPath,
      },
    });
  };
};
